import { faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { useTheme } from '@mui/material';
import { useEffect, useState } from 'react';

import { getDocument } from '../../api/documentApi';
import { getInsuranceService } from '../../api/getInsurance';
import Description from '../../components/Description/Description';
import Icon from '../../components/Icon/Icon';
import Link from '../../components/Link/Link';
import ListField from '../../components/ListField/ListField';
import RadioField from '../../components/RadioField/RadioField';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { GetDocumentRequest } from '../../domain/types';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { Insurance } from '../../types/InsuranceTypes';

const PpiPage = () => {
    const translate = useTranslation();
    const theme = useTheme();
    const { languageCode, apiKey, proxyBaseUrl, countryCode } = useSettings();
    const [insuranceDetails, setInsuranceDetails] = useState<Insurance | undefined>(undefined);
    const applicationReferenceNumber = useArnFromPath();

    useEffect(() => {
        getInsuranceService(proxyBaseUrl, apiKey, applicationReferenceNumber).then(setInsuranceDetails);
    }, []);

    const value = useAppSelector(selectFieldStateByName)('paymentProtectionInsurance')?.value;

    const downloadDocument = (
        documentType: 'ppiProductFactSheet' | 'ppiPreAndPostPurchase' | 'ppiMediation' | 'ppiTerms',
    ) => {
        const data = {};
        getDocument({
            apiProxyPath: proxyBaseUrl,
            apiKey,
            countryCode,
            languageCode,
            documentType,
            data,
        } as GetDocumentRequest);
    };

    return (
        <>
            <Title text={translate('page.payment-protection-insurance.title')} />
            <Description
                text={translate('page.payment-protection-insurance.description', {
                    duration: insuranceDetails?.duration.months,
                    percentage: insuranceDetails?.percentageFee,
                    minFees: formatNumberByThousands(insuranceDetails?.minimumFee?.amount ?? ''),
                    maxFees: formatNumberByThousands(insuranceDetails?.maximumFee?.amount ?? ''),
                })}
            />
            <ShowDetailsModal
                dialogTitle={translate('dialog.payment-protection-insurance.label')}
                label={translate('dialog.payment-protection-insurance.label')}
                dialogContent={translate('dialog.payment-protection-insurance.content')}
            />
            <RadioField
                name="paymentProtectionInsurance"
                options={[
                    { id: 'true', label: translate('field.payment-protection-insurance.option.yes.label') },
                    { id: 'false', label: translate('field.payment-protection-insurance.option.no.label') },
                ]}
            />
            {value === 'true' && (
                <>
                    <ListField
                        name="paymentProtectionInsuranceConditions"
                        hideDivider={true}
                        items={[
                            {
                                index: '1',
                                icon: <Icon icon={faFileLines} color={theme.palette.primary.light} />,
                                label: (
                                    <Link color="secondary" onClick={() => downloadDocument('ppiProductFactSheet')}>
                                        {translate(
                                            'field.payment-protection-insurance.pdf.document-product-fact-sheet.label',
                                        )}
                                    </Link>
                                ),
                            },
                            {
                                index: '2',
                                icon: <Icon icon={faFileLines} color={theme.palette.primary.light} />,
                                label: (
                                    <Link color="secondary" onClick={() => downloadDocument('ppiPreAndPostPurchase')}>
                                        {translate(
                                            'field.payment-protection-insurance.pdf.document-purchase-information.label',
                                        )}
                                    </Link>
                                ),
                            },
                            {
                                index: '3',
                                icon: <Icon icon={faFileLines} color={theme.palette.primary.light} />,
                                label: (
                                    <Link color="secondary" onClick={() => downloadDocument('ppiMediation')}>
                                        {translate(
                                            'field.payment-protection-insurance.pdf.document-insurance-information',
                                        )}
                                    </Link>
                                ),
                            },
                            {
                                index: '4',
                                icon: <Icon icon={faFileLines} color={theme.palette.primary.light} />,
                                label: (
                                    <Link color="secondary" onClick={() => downloadDocument('ppiTerms')}>
                                        {translate('field.payment-protection-insurance.pdf.document-insurance-terms')}
                                    </Link>
                                ),
                            },
                        ]}
                    />
                </>
            )}
        </>
    );
};

export default PpiPage;
